import {
    Box,
    FormControl,
    FormLabel,
    Input,
    List,
    ListItem,
    Spinner,
    InputGroup
} from '@chakra-ui/react';
import { FormikContext } from 'formik';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from "react-i18next";
import axios from 'axios';

const pathExists = (obj, path) => {
    if (!obj || !path) return false;
    return path.split('.').every(k => (obj = obj?.[k]) !== undefined);
};

const AddressAutocompleteInput = ({ name, label, flex, isRequired = false, value, onChange, onBlur, error, touched, useFormik = true, isDisabled = false, ...props }) => {
    const { t } = useTranslation("default");
    const formik = useContext(FormikContext);
    const inFormik = !!(useFormik && formik && name && pathExists(formik.values, name));

    const fieldProps = inFormik ? formik.getFieldProps(name) : { value, onChange, onBlur };
    const meta = inFormik ? formik.getFieldMeta(name) : { error, touched };

    const showError = !!(meta.touched && meta.error);

    const [inputValue, setInputValue] = useState(fieldProps.value || '');
    const [suggestions, setSuggestions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);

    const wrapperRef = useRef(null);
    const debounceTimeoutRef = useRef(null);

    useEffect(() => {
        setInputValue(fieldProps.value || '');
    }, [fieldProps.value]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const fetchSuggestions = async (query) => {
        if (!query || query.length < 3) {
            setSuggestions([]);
            setShowSuggestions(false);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        try {
            const response = await axios.get('https://nominatim.openstreetmap.org/search', {
                params: {
                    q: query,
                    format: 'json',
                    addressdetails: 1,
                    limit: 5,
                    "accept-language": "pl"
                }
            });
            setSuggestions(response.data);
            setShowSuggestions(true);
        } catch (error) {
            console.error("Error fetching address suggestions:", error);
            setSuggestions([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const val = e.target.value;
        setInputValue(val);

        if (inFormik) {
            formik.setFieldValue(name, val);
        } else if (onChange) {
            onChange(e);
        }

        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        debounceTimeoutRef.current = setTimeout(() => {
            fetchSuggestions(val);
        }, 500);
    };

    const handleSuggestionClick = (suggestion) => {
        let addressName = suggestion.display_name;

        if (suggestion.address) {
            const road = suggestion.address.road || suggestion.address.pedestrian || suggestion.address.street || '';
            const houseNumber = suggestion.address.house_number || '';
            const postcode = suggestion.address.postcode || '';
            const city = suggestion.address.city || suggestion.address.town || suggestion.address.village || '';

            let firstPart = suggestion.display_name.split(',')[0].trim();

            let parts = [];

            if (firstPart !== road && firstPart !== city && firstPart !== houseNumber && firstPart !== postcode) {
                parts.push(firstPart);
            }

            let streetPart = road;
            if (road && houseNumber) {
                streetPart = `${road} ${houseNumber}`;
            } else if (houseNumber) {
                streetPart = houseNumber;
            }

            if (streetPart) {
                parts.push(streetPart);
            }

            let cityPart = city;
            if (postcode && city) {
                cityPart = `${postcode} ${city}`;
            } else if (postcode) {
                cityPart = postcode;
            }

            if (cityPart) {
                parts.push(cityPart);
            }

            if (parts.length > 0) {
                addressName = [...new Set(parts)].join(', ');
            }
        }

        setInputValue(addressName);
        if (inFormik) {
            formik.setFieldValue(name, addressName);
            formik.setFieldTouched(name, true);
        } else if (onChange) {
            onChange({ target: { name, value: addressName } });
        }

        setSuggestions([]);
        setShowSuggestions(false);
    };

    const handleInputBlur = (e) => {
        if (inFormik) {
            formik.handleBlur(e);
        } else if (onBlur) {
            onBlur(e);
        }
    };

    return (
        <FormControl isInvalid={showError} isRequired={isRequired} isDisabled={isDisabled} flex={flex}>
            <FormLabel htmlFor={name}>{label}</FormLabel>
            <Box position="relative" ref={wrapperRef}>
                <InputGroup>
                    <Input
                        id={name}
                        type="text"
                        name={name}
                        value={inputValue}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                        autoComplete="off"
                        aria-invalid={showError ? 'true' : undefined}
                        aria-describedby={showError ? `${name}-error` : undefined}
                        isDisabled={isDisabled}
                        {...props}
                    />
                    {isLoading && (
                        <Box position="absolute" right="10px" top="50%" transform="translateY(-50%)" zIndex={2}>
                            <Spinner size="sm" color="gray.400" />
                        </Box>
                    )}
                    {showError && (
                        <Box
                            id={`${name}-error`}
                            position="absolute"
                            top="4px"
                            right={isLoading ? "40px" : "6px"}
                            textAlign={"right"}
                            color="red.500"
                            fontSize="xs"
                            zIndex={1}
                            pointerEvents="none">
                            {meta.error}
                        </Box>
                    )}
                </InputGroup>

                {showSuggestions && suggestions.length > 0 && !isDisabled && (
                    <List
                        position="absolute"
                        top="100%"
                        left={0}
                        right={0}
                        mt={1}
                        bg="white"
                        boxShadow="md"
                        borderRadius="md"
                        zIndex={1000}
                        maxH="200px"
                        overflowY="auto"
                        border="1px solid"
                        borderColor="gray.200"
                    >
                        {suggestions.map((suggestion) => (
                            <ListItem
                                key={suggestion.place_id}
                                px={4}
                                py={2}
                                cursor="pointer"
                                _hover={{ bg: "gray.100" }}
                                onClick={() => handleSuggestionClick(suggestion)}
                                fontSize="sm"
                                borderBottom="1px solid"
                                borderColor="gray.100"
                                _last={{ borderBottom: "none" }}
                            >
                                {suggestion.display_name}
                            </ListItem>
                        ))}
                    </List>
                )}
            </Box>
        </FormControl>
    );
};

export default AddressAutocompleteInput;
